// src/pages/CardDetail.js

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getCardDetails, getAllTransactions } from '../api/CardAPI';
import {
  Box,
  Button,
  Typography,
  Container,
  IconButton,
  Chip,
  Skeleton,
  Paper,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
} from '@mui/material';
import {
  ArrowBackOutlined,
  ContentCopyOutlined,
  VisibilityOutlined,
  VisibilityOffOutlined,
  DownloadOutlined,
  ShareOutlined,
  ReceiptLongOutlined,
  TrendingUpOutlined,
  TrendingDownOutlined,
  FilterListOutlined,
  CheckCircleOutlined,
  LocalAtmOutlined,
  AccountBalanceWalletOutlined,
  ShoppingBagOutlined,
  RestaurantOutlined,
  DirectionsCarOutlined,
  HomeOutlined,
  MoreVertOutlined,
  NotificationsOutlined,
  SwapHoriz,
  PaymentsOutlined,
  AccountBalanceWallet,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
  ArcElement
);

// Flippable Card Component
function FlippableCard({ cardDetails, showNumber, onToggleNumber, currentUser }) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showCVV, setShowCVV] = useState(false);

  const formatCardNumber = (number) => {
    if (!number) return '•••• •••• •••• ••••';
    if (showNumber) {
      return number.replace(/(.{4})/g, '$1 ').trim();
    }
    return `•••• •••• •••• ${number.slice(-4)}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box
      sx={{
        perspective: '1000px',
        WebkitPerspective: '1000px',
        width: '100%',
        maxWidth: 450,
        height: 280,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          transformStyle: 'preserve-3d',
          WebkitTransformStyle: 'preserve-3d',
          transition: 'transform 0.6s',
          WebkitTransition: '-webkit-transform 0.6s',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          WebkitTransform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front Side */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            MozBackfaceVisibility: 'hidden',
            OBackfaceVisibility: 'hidden',
            msBackfaceVisibility: 'hidden',
            zIndex: isFlipped ? 1 : 2,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
            boxShadow: '0 20px 40px rgba(30, 58, 138, 0.4)',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'hidden',
            cursor: 'pointer',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -100,
              right: -100,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -50,
              left: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.05)',
            },
          }}
          onClick={() => setIsFlipped(!isFlipped)}
        >
          {/* Card Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', zIndex: 1 }}>
            <Box>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                {cardDetails?.cardName || 'Premium Card'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                {cardDetails?.cardBrand || 'Virtual Card'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleNumber();
                }}
                sx={{ color: 'white', bgcolor: 'rgba(255, 255, 255, 0.2)' }}
              >
                {showNumber ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(cardDetails?.cardNo || '');
                }}
                sx={{ color: 'white', bgcolor: 'rgba(255, 255, 255, 0.2)' }}
              >
                {copied ? <CheckCircleOutlined /> : <ContentCopyOutlined />}
              </IconButton>
            </Box>
          </Box>

          {/* Card Number */}
          <Box sx={{ zIndex: 1 }}>
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontFamily: 'monospace',
                letterSpacing: '0.15em',
                textShadow: '0 2px 6px rgba(0,0,0,0.4)',
                fontSize: '1.5rem',
                fontWeight: 500,
              }}
            >
              {formatCardNumber(cardDetails?.cardNo)}
            </Typography>
          </Box>

          {/* Card Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', zIndex: 1 }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.7rem' }}>
                VALID THRU
              </Typography>
              <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                {cardDetails?.expDate || 'MM/YY'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Box
                component="img"
                src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'%3E%3Cpath fill='%23ff5722' d='M32 10A14 14 0 1 0 32 38A14 14 0 1 0 32 10Z'/%3E%3Cpath fill='%23ffeb3b' d='M16 10A14 14 0 1 0 16 38A14 14 0 1 0 16 10Z'/%3E%3C/svg%3E"
                sx={{ width: 50, height: 30, mb: 1 }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', display: 'block', fontSize: '0.65rem' }}>
                Click to see back
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Back Side */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            MozBackfaceVisibility: 'hidden',
            OBackfaceVisibility: 'hidden',
            msBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            WebkitTransform: 'rotateY(180deg)',
            zIndex: isFlipped ? 2 : 1,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
            boxShadow: '0 10px 30px rgba(30, 58, 138, 0.3)',
            p: 0,
            cursor: 'pointer',
            overflow: 'hidden',
          }}
          onClick={() => setIsFlipped(!isFlipped)}
        >
          {/* Magnetic Strip */}
          <Box
            sx={{
              width: '100%',
              height: 60,
              bgcolor: '#000',
              mt: 3,
            }}
          />

          {/* Signature Box */}
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                bgcolor: 'white',
                height: 40,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                px: 2,
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: '#666' }}>
                Authorized Signature
              </Typography>
            </Box>

            {/* CVV */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCVV(!showCVV);
                }}
                sx={{
                  bgcolor: 'white',
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                  {showCVV && cardDetails?.cvv ? cardDetails.cvv : '•••'}
                </Typography>
                <IconButton 
                  size="small" 
                  sx={{ p: 0.5, color: '#666' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCVV(!showCVV);
                  }}
                >
                  {showCVV ? <VisibilityOffOutlined sx={{ fontSize: 16 }} /> : <VisibilityOutlined sx={{ fontSize: 16 }} />}
                </IconButton>
              </Box>
            </Box>

            {/* Security Info */}
            <Typography variant="caption" sx={{ color: 'white', display: 'block', textAlign: 'center' }}>
              This card is property of CardsPay Inc.
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', textAlign: 'center', mt: 1 }}>
              Click to see front
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Transaction Category Icon
function getCategoryIcon(category) {
  const icons = {
    'shopping': <ShoppingBagOutlined />,
    'food': <RestaurantOutlined />,
    'transport': <DirectionsCarOutlined />,
    'bills': <HomeOutlined />,
    'entertainment': <LocalAtmOutlined />,
    'transfer': <SwapHoriz />,
    'payment': <PaymentsOutlined />,
    'deposit': <AccountBalanceWallet />,
    'other': <MoreVertOutlined />,
  };
  const categoryLower = (category || 'other').toLowerCase();
  return icons[categoryLower] || icons.other;
}

// Currency symbol helper
function getCurrencySymbol(currency) {
  const symbols = {
    'USD': '$',
    'ILS': '₪',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CNY': '¥',
    'INR': '₹',
    'AUD': 'A$',
    'CAD': 'C$',
    'CHF': 'Fr',
    'SEK': 'kr',
    'NOK': 'kr',
    'DKK': 'kr',
    'RUB': '₽',
    'BRL': 'R$',
    'MXN': '$',
    'ARS': '$',
    'CLP': '$',
    'COP': '$',
    'PEN': 'S/',
    'UYU': '$',
    'ZAR': 'R',
    'TRY': '₺',
    'AED': 'د.إ',
    'SAR': 'ر.س',
    'QAR': 'ر.ق',
    'KWD': 'د.ك',
    'BHD': 'د.ب',
    'OMR': 'ر.ع',
    'JOD': 'د.ا',
    'EGP': 'E£',
    'THB': '฿',
    'SGD': 'S$',
    'MYR': 'RM',
    'IDR': 'Rp',
    'PHP': '₱',
    'VND': '₫',
    'KRW': '₩',
    'HKD': 'HK$',
    'TWD': 'NT$',
    'NZD': 'NZ$'
  };
  
  return symbols[currency] || currency || '$'; // Default to currency code or $ if not found
}

// Stats Card Component
function StatsCard({ title, value, change, icon, color }) {
  const isPositive = change >= 0;
  
  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Avatar
          sx={{
            bgcolor: `${color}15`,
            color: color,
            width: 48,
            height: 48,
          }}
        >
          {icon}
        </Avatar>
        <Chip
          size="small"
          icon={isPositive ? <TrendingUpOutlined /> : <TrendingDownOutlined />}
          label={`${isPositive ? '+' : ''}${change}%`}
          sx={{
            bgcolor: isPositive ? '#22C55E20' : '#EF444420',
            color: isPositive ? '#22C55E' : '#EF4444',
            fontWeight: 600,
          }}
        />
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748B' }}>
        {title}
      </Typography>
    </Paper>
  );
}

export default function CardDetail() {
  const { san } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [cardDetails, setCardDetails] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  // Fetch card details
  useEffect(() => {
    const fetchCardData = async () => {
      if (!currentUser || !san) return;
      
      try {
        
        // Fetch user's card data from Firestore
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        let cardName = '';
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const card = userData.cards?.find(c => c.san === san);
          if (card) {
            cardName = card.cardName || '';
          }
        }
        
        // Fetch card details from API
        const details = await getCardDetails(san);
        // Add card name from Firestore to the details
        setCardDetails({
          ...details,
          cardName: cardName || details.cardName
        });
      } catch (error) {
        // Error logging removed for security
      } finally {
        setLoading(false);
      }
    };

    fetchCardData();
  }, [san, currentUser]);

  // Fetch transactions separately
  useEffect(() => {
    const fetchTransactionData = async () => {
      if (!san) return;
      
      try {
        setLoadingTransactions(true);
        
        // Fetch ALL transactions - no limit
        const txns = await getAllTransactions(san, 50); // Get up to 50 pages of transactions
        
        
        
        
        
        
        // Remove duplicates based on transaction ID
        const uniqueTxns = txns.reduce((acc, current) => {
          const x = acc.find(item => 
            item.id === current.id
          );
          if (!x) {
            return acc.concat([current]);
          } else {
            return acc;
          }
        }, []);
        
        
        // Sort by date (newest first)
        uniqueTxns.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        
        setTransactions(uniqueTxns);
      } catch (error) {
        // Error logging removed for security
      } finally {
        setLoadingTransactions(false);
      }
    };

    fetchTransactionData();
  }, [san]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };



  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', pt: 12 }}>
        <Container maxWidth="lg">
          <Skeleton variant="rounded" height={250} sx={{ mb: 4, borderRadius: 4 }} />
          <Grid container spacing={3}>
            {[...Array(4)].map((_, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, #F1F5F9 0%, #F8FAFC 100%)',
          pt: 12,
          pb: 6,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
              <ArrowBackOutlined />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1E293B' }}>
                Card Details
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                Manage your card and view transactions
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton sx={{ border: '1px solid var(--border-primary)' }}>
                <ShareOutlined />
              </IconButton>
              <IconButton sx={{ border: '1px solid var(--border-primary)' }}>
                <DownloadOutlined />
              </IconButton>
              <IconButton sx={{ border: '1px solid var(--border-primary)' }}>
                <NotificationsOutlined />
              </IconButton>
            </Box>
          </Box>

          {/* Flippable Card */}
          <FlippableCard
            cardDetails={cardDetails}
            showNumber={showCardNumber}
            onToggleNumber={() => setShowCardNumber(!showCardNumber)}
            currentUser={currentUser}
          />
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -2 }}>
        {/* Stats Grid */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6}>
            <StatsCard
              title="Current Balance"
              value={`${getCurrencySymbol(cardDetails?.currency)}${(cardDetails?.cardBal || 0).toLocaleString()}`}
              change={12.5}
              icon={<AccountBalanceWalletOutlined />}
              color="#1e3a8a"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StatsCard
              title="Transactions"
              value={transactions.length}
              change={8.7}
              icon={<ReceiptLongOutlined />}
              color="#3b82f6"
            />
          </Grid>
        </Grid>

        {/* Main Content Grid */}
        <Grid container spacing={4}>
          {/* Transactions Table */}
          <Grid item xs={12}>
            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 3, borderBottom: '1px solid #E2E8F0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Recent Transactions
                  </Typography>
                  <Button
                    startIcon={<FilterListOutlined />}
                    sx={{ textTransform: 'none' }}
                  >
                    Filter
                  </Button>
                </Box>
              </Box>
              
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: { xs: 300, sm: 600 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Transaction</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingTransactions ? (
                      <>
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <CircularProgress size={40} />
                            <Typography variant="body2" sx={{ mt: 2, color: '#64748B' }}>
                              Loading all transactions...
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </>
                    ) : false ? (
                      [...Array(5)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Skeleton variant="circular" width={40} height={40} />
                              <Box>
                                <Skeleton width={150} />
                                <Skeleton width={100} height={16} />
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell><Skeleton width={80} /></TableCell>
                          <TableCell align="right"><Skeleton width={80} /></TableCell>
                          <TableCell align="right"><Skeleton width={70} /></TableCell>
                        </TableRow>
                      ))
                    ) : transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No transactions found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((transaction, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: '#F1F5F9' }}>
                                {getCategoryIcon(transaction.category)}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {transaction.title || 'Card Transaction'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                  {transaction.currency || 'USD'} • ID: {transaction.id ? transaction.id.substring(0, 8) : `${index + 1}`}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {transaction.date ? new Date(transaction.date).toLocaleDateString() : 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                              {transaction.date ? new Date(transaction.date).toLocaleTimeString() : ''}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: transaction.amount < 0 ? '#22C55E' : '#EF4444',
                              }}
                            >
                              {transaction.amount < 0 ? '+' : '-'}{(() => {
                                // Use original amount if available, otherwise use amount
                                const displayAmount = transaction.originalAmount !== undefined ? transaction.originalAmount : transaction.amount;
                                const displayCurrency = transaction.originalCurrency || transaction.currency;
                                const currencySymbol = getCurrencySymbol(displayCurrency);
                                return `${currencySymbol}${Math.abs(displayAmount || 0).toFixed(2)}`;
                              })()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={(() => {
                                // Check if it's pending (amount 0 with status 1)
                                if (transaction.amount === 0 && transaction.status === "1") {
                                  return "Pending";
                                }
                                // Otherwise use status
                                return transaction.status === "1" ? "Completed" : "Failed";
                              })()}
                              size="small"
                              icon={(() => {
                                const isCompleted = transaction.status === "1" && transaction.amount !== 0;
                                return isCompleted ? <CheckCircleOutlined sx={{ fontSize: 16 }} /> : null;
                              })()}
                              sx={{
                                bgcolor: (() => {
                                  if (transaction.amount === 0 && transaction.status === "1") {
                                    return '#F59E0B20'; // Orange for pending
                                  }
                                  return transaction.status === "1" ? '#22C55E20' : '#EF444420';
                                })(),
                                color: (() => {
                                  if (transaction.amount === 0 && transaction.status === "1") {
                                    return '#F59E0B'; // Orange for pending
                                  }
                                  return transaction.status === "1" ? '#22C55E' : '#EF4444';
                                })(),
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={transactions.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          </Grid>

        </Grid>
      </Container>

    </Box>
  );
}
